import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

const DISCLAIMER_ACKNOWLEDGEMENTS = [
  {
    id: 'attempt-counts-on-start',
    label:
      'I understand that once the examination has started, it counts as one attempt even if I do not finish it.',
  },
  {
    id: 'timed-no-documents',
    label:
      'I understand that the examination is timed and that I will not be able to use course documents during the examination.',
  },
];

export default function AcademyExamDisclaimerModal({ show, course, onCancel, onConfirm }) {
  const [acknowledgements, setAcknowledgements] = useState({});

  const portalTarget =
    typeof window !== 'undefined' && window.document
      ? window.document.body || window.document.getElementById('root')
      : null;

  useEffect(() => {
    if (!show) {
      setAcknowledgements({});
    }
  }, [show]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.document?.body) return undefined;
    if (show) window.document.body.classList.add('modal-open');
    else window.document.body.classList.remove('modal-open');
    return () => window.document.body.classList.remove('modal-open');
  }, [show]);

  useEffect(() => {
    if (!show || typeof window === 'undefined' || !window.document) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel();
    };
    window.document.addEventListener('keydown', onKey);
    return () => window.document.removeEventListener('keydown', onKey);
  }, [show, onCancel]);

  const allAcknowledged = useMemo(
    () =>
      (unlimitedAttempts || Boolean(acknowledgements['attempt-limit'])) &&
      DISCLAIMER_ACKNOWLEDGEMENTS.every((item) => Boolean(acknowledgements[item.id])),
    [acknowledgements, unlimitedAttempts]
  );

  if (!show || !course || !portalTarget) return null;

  const courseTitle = course.displayName || course.courseName || 'Course';
  const attemptsMax = course.examAttemptsMax ?? 0;
  const attemptsUsed = course.examAttemptsUsed ?? 0;
  const unlimitedAttempts = attemptsMax === 0;

  return ReactDOM.createPortal(
    <div
      className="course-exam-modal-overlay member-academy-exam-disclaimer-overlay"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="member-academy-exam-disclaimer"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="member-academy-exam-disclaimer-title"
      >
        <header className="member-academy-exam-disclaimer__header">
          <p className="member-academy-exam-disclaimer__eyebrow">Examination disclaimer</p>
          <h2 id="member-academy-exam-disclaimer-title" className="member-academy-exam-disclaimer__title">
            {courseTitle}
          </h2>
          <button
            type="button"
            className="course-exam-modal__close"
            onClick={onCancel}
            aria-label="Close"
          />
        </header>

        <div className="member-academy-exam-disclaimer__body">
          <p className="member-academy-exam-disclaimer__intro">
            Before entering the examination room, please read the following rules carefully.
          </p>

          <ul className="member-academy-exam-disclaimer__rules">
            <li>
              {unlimitedAttempts ? (
                <>
                  There is <strong>no limit</strong> on the number of attempts for this examination.
                </>
              ) : (
                <>
                  You may only use <strong>{attemptsMax} attempts</strong> for this examination in total.
                </>
              )}
            </li>
            <li>
              Once an attempt has started, closing this page, reloading, navigating away, or leaving the
              site will count as a failed attempt.
            </li>
            <li>
              {unlimitedAttempts ? (
                <>
                  Attempts used so far: <strong>{attemptsUsed}</strong>.
                </>
              ) : (
                <>
                  Attempts used so far: <strong>{attemptsUsed}</strong> / {attemptsMax}.
                </>
              )}
            </li>
          </ul>

          <div className="member-academy-exam-disclaimer__checks">
            {!unlimitedAttempts ? (
              <label className="member-academy-exam-disclaimer__check">
                <input
                  type="checkbox"
                  className="member-academy-exam-disclaimer__checkbox"
                  checked={Boolean(acknowledgements['attempt-limit'])}
                  onChange={(event) =>
                    setAcknowledgements((current) => ({
                      ...current,
                      'attempt-limit': event.target.checked,
                    }))
                  }
                />
                <span>
                  I understand that I only have {attemptsMax} attempt{attemptsMax === 1 ? '' : 's'} in
                  total for this examination.
                </span>
              </label>
            ) : null}

            {DISCLAIMER_ACKNOWLEDGEMENTS.map((item) => (
              <label key={item.id} className="member-academy-exam-disclaimer__check">
                <input
                  type="checkbox"
                  className="member-academy-exam-disclaimer__checkbox"
                  checked={Boolean(acknowledgements[item.id])}
                  onChange={(event) =>
                    setAcknowledgements((current) => ({
                      ...current,
                      [item.id]: event.target.checked,
                    }))
                  }
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        <footer className="member-academy-exam-disclaimer__footer">
          <button
            type="button"
            className="accounting-btn accounting-btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="accounting-btn accounting-btn--primary"
            disabled={!allAcknowledged}
            onClick={() => onConfirm(course)}
            data-resource="private.navbar.academy"
            data-action="execute"
            data-route="/api/user/academy/start-course-exam"
            data-description="Academy → Confirm examination disclaimer"
          >
            Begin examination
          </button>
        </footer>
      </div>
    </div>,
    portalTarget
  );
}

export { DISCLAIMER_ACKNOWLEDGEMENTS };
